import React, {
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle,
} from 'react'
import {
    Country,
    State,
    City
} from 'country-state-city'

const countriesWithoutStates = [
    'United Kingdom',
    'Netherlands',
    'Belgium',
    'Denmark',
    'Sweden',
    'Norway',
    'Finland',
    'Ireland',
]

const LocationSelector = forwardRef(({ data, readonly }, ref) => {
    const [countries, setCountries] = useState([])
    const [states, setStates] = useState([])
    const [cities, setCities] = useState([])

    const [selectedCountry, setSelectedCountry] = useState(data?.country)
    const [selectedState, setSelectedState] = useState(data?.state)
    const [selectedCity, setSelectedCity] = useState(data?.city)
    const [zipCode, setZipCode] = useState(data?.zipCode ?? "")
    const [error, setError] = useState(false)

    const isStateRequired = selectedCountry
        ? !countriesWithoutStates.includes(selectedCountry.name)
        : true

    useEffect(() => {
        const allCountries = Country.getAllCountries()
        setCountries(allCountries)

        let defaultCountry = allCountries.find(c => c.isoCode === 'US')

        if (data?.country) {
            const preselected = allCountries.find(c => c.name === data?.country)
            if (preselected) defaultCountry = preselected
        }

        setSelectedCountry(defaultCountry)
    }, [data?.country])

    useEffect(() => {
        if (!selectedCountry) {
            setStates([])
            setSelectedState(null)
            setCities([])
            setSelectedCity(null)
            return
        }

        const countryStates = State.getStatesOfCountry(selectedCountry.isoCode)
        setStates(countryStates)

        if (!isStateRequired || countryStates.length === 0) {
            setSelectedState(null)
        } else if (data?.state) {
            const matchedState = countryStates.find(s => s.name === data?.state)
            setSelectedState(matchedState || null)
        } else {
            setSelectedState(null)
        }

        setCities([])
        setSelectedCity(null)
    }, [selectedCountry, data?.state, isStateRequired])

    useEffect(() => {
        if (!selectedCountry) {
            setCities([])
            setSelectedCity(null)
            return
        }

        let cityList = []

        if (!isStateRequired) {
            cityList = City.getCitiesOfCountry(selectedCountry.isoCode)
        } else if (selectedState) {
            cityList = City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode)
        }

        setCities(cityList)

        if (data?.city) {
            const matchedCity = cityList.find(c => c.name === data?.city)
            setSelectedCity(matchedCity || null)
        } else {
            setSelectedCity(null)
        }
    }, [selectedState, selectedCountry, data?.city, isStateRequired])

    useEffect(() => {
        setZipCode(data.zipCode || '')
    }, [data?.zipCode])

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {
            const finalZip = zipCode.trim()

            const isValid =
                selectedCountry &&
                (!isStateRequired || selectedState) &&
                selectedCity &&
                finalZip !== ''

            setError(!isValid)

            return {
                isValid,
                value: {
                    country: selectedCountry?.name,
                    state: selectedState?.name || '',
                    city: selectedCity?.name,
                    zipCode: finalZip
                }
            }
        }
    }))

    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Location
            </label>

            <div className="grid grid-cols-2 gap-4">
                {readonly ? (
                    <input
                        type="text"
                        readOnly
                        className="border border-gray-300 rounded-md p-2 bg-gray-100 w-full"
                        value={selectedCountry?.name || ''}
                    />
                ) : (
                    <select
                        className="border border-gray-300 rounded-md p-2 bg-white w-full"
                        value={selectedCountry?.isoCode || ''}
                        onChange={e => {
                            const selected = countries.find(c => c.isoCode === e.target.value)
                            setSelectedCountry(selected)
                            setSelectedState(null)
                            setSelectedCity(null)
                            setCities([])
                            setZipCode('')
                            setError(false)
                        }}
                    >
                        <option value="">Select Country</option>
                        {countries.map(country => (
                            <option key={country.isoCode} value={country.isoCode}>
                                {country.name}
                            </option>
                        ))}
                    </select>
                )}

                {readonly ? (
                    <input
                        type="text"
                        readOnly
                        className="border border-gray-300 rounded-md p-2 bg-gray-100 w-full"
                        value={selectedState?.name || ''}
                    />
                ) : (
                    <select
                        className="border border-gray-300 rounded-md p-2 bg-white w-full"
                        value={selectedState?.isoCode || ''}
                        onChange={e => {
                            const selected = states.find(s => s.isoCode === e.target.value)
                            setSelectedState(selected)
                            setSelectedCity(null)
                            setCities([])
                            setZipCode('')
                            setError(false)
                        }}
                        disabled={!isStateRequired || states.length === 0}
                    >
                        <option value="">Select State</option>
                        {states.map(state => (
                            <option key={state.isoCode} value={state.isoCode}>
                                {state.name}
                            </option>
                        ))}
                    </select>
                )}

                {readonly ? (
                    <input
                        type="text"
                        readOnly
                        className="border border-gray-300 rounded-md p-2 bg-gray-100 w-full"
                        value={selectedCity?.name || ''}
                    />
                ) : (
                    <select
                        className="border border-gray-300 rounded-md p-2 bg-white w-full"
                        value={selectedCity?.name || ''}
                        onChange={e => {
                            const selected = cities.find(c => c.name === e.target.value)
                            setSelectedCity(selected)
                            setZipCode('')
                            setError(false)
                        }}
                        disabled={cities.length === 0}
                    >
                        <option value="">Select City</option>
                        {cities.map(city => (
                            <option key={city.name} value={city.name}>
                                {city.name}
                            </option>
                        ))}
                    </select>
                )}

                {readonly ? (
                    <input
                        type="text"
                        readOnly
                        className="border border-gray-300 rounded-md p-2 bg-gray-100 w-full"
                        value={zipCode}
                    />
                ) : (
                    <input
                        type="text"
                        className="border border-gray-300 rounded-md p-2 bg-white w-full"
                        placeholder='Enter Zip Code, fill "00000" if NA'
                        value={zipCode}
                        onChange={e => {
                            setZipCode(e.target.value)
                            setError(false)
                        }}
                    />
                )}
            </div>

            {error && (
                <p className="text-sm text-red-600 mt-2">Fill location fields correctly</p>
            )}
        </div>
    )
})

export default LocationSelector
